<template>
  <form @submit.prevent="handleSubmit" id="edit_response_form">
    <!-- Response Name -->
    <div class="mb-3">
      <label class="form-label form-label-thin">Response Name <span class="text-danger">*</span></label>
      <input type="text" class="form-control form-control-thin" v-model="form.name" placeholder="Enter response name" required />
    </div>

    <!-- Scoring Checkbox -->
    <div class="mb-3">
      <label class="checkbox-label">
        <input type="checkbox" v-model="form.enable_scoring" />
        <span class="checkmark"></span>
        Enable Scoring
      </label>
    </div>

    <!-- Options -->
    <div class="mb-3">
      <label class="form-label form-label-thin">Options <span class="text-danger">*</span></label>
      <div class="options-container">
        <div v-for="(option, index) in form.options" :key="index" class="option-item">
          <div class="option-row">
            <input 
              type="color" 
              class="color-picker" 
              v-model="option.bg_color" 
            />
            <input 
              type="text" 
              class="form-control form-control-thin form-control-thin-name" 
              v-model="option.name" 
              :placeholder="`Option ${index + 1}`"
              required
            />
            <input 
              v-if="form.enable_scoring"
              type="number" 
              class="form-control form-control-thin score-input" 
              v-model="option.score" 
              placeholder="Score (1-100)"
              min="0"
              max="100"
            />
            <button type="button" class="btn-remove" @click="removeOption(index)">
              <i class="fa fa-trash"></i>
            </button>
          </div>
        </div>
      </div>
      <div class="text-end">
        <button type="button" class="btn btn-light py-2 btn-sm" @click="addOption">
          + Add Option
        </button>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="modal-footer">
      <button type="button" class="btn btn-outline-danger btn-sm hover-white" @click="close" :disabled="submitting">
        <i class="fa fa-times"></i> Cancel
      </button>
      <button type="submit" class="btn btn-primary text-light hover-white btn-md form-submit-btn" :disabled="submitting">
        <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
        <i v-else class="fa fa-save me-1"></i>
        {{ submitting ? 'Updating...' : 'Update Response' }}
      </button>
    </div>
  </form>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'

export default {
  name: 'EditResponse',
  emits: ['updated', 'close'],
  data() {
    return {
      form: {
        name: '',
        enable_scoring: false,
        options: []
      },
      submitting: false,
      responseId: null
    }
  },
  methods: {
    setResponseData(response) {
      this.responseId = response.id
      this.form.name = response.name || ''
      this.form.enable_scoring = response.scoring || false
      this.form.options = (response.get_options || []).map(opt => ({
        id: opt.id,
        name: opt.name || '',
        bg_color: opt.bg || '#667eea',
        color: opt.color || null,
        score: opt.score || null,
        flag: opt.flag || false
      }))
      
      if (this.form.options.length === 0) {
        this.form.options.push({
          name: '',
          bg_color: '#667eea',
          score: null
        })
      }
    },

    resetForm() {
      this.responseId = null
      this.form = {
        name: '',
        enable_scoring: false,
        options: []
      }
    },

    addOption() {
      this.form.options.push({
        name: '',
        bg_color: '#667eea',
        score: null
      })
    },

    removeOption(index) {
      if (this.form.options.length > 1) {
        this.form.options.splice(index, 1)
      }
    },

    async handleSubmit() {
      if (!this.responseId) {
        this.$toast('Invalid response ID', false)
        return
      }

      if (!this.form.name.trim()) {
        this.$toast('Please enter a response name', false)
        return
      }

      if (this.form.options.length === 0) {
        this.$toast('Please add at least one option', false)
        return
      }

      this.submitting = true

      try {
        const result = await form_api_submit({
          url: 'template/builder/response/edit/' + this.responseId,
          method: 'POST',
          data: this.form,
          // Remove curr_form_id to avoid DOM manipulation errors
          // curr_form_id: '#edit_response_form',
          show_success_toast: true,
          show_error_toast: true
        })

        if (result.success) {
          this.$emit('updated', result.data)
          this.close()
          this.resetForm()
        }
      } catch (error) {
        console.error('Error updating response:', error)
        this.$toast('Failed to update response', false)
      } finally {
        this.submitting = false
      }
    },

    close() {
      this.resetForm()
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.form-label-thin {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 4px;
}

.form-control-thin {
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1.5px solid #e0e0e0;
  transition: border-color 0.2s;
}

.form-control-thin:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.options-container {
  margin-bottom: 10px;
}

.option-item {
  margin-bottom: 10px;
}

.option-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.option-row .form-control-thin {
  flex: 1;
}

.option-row .form-control-thin-name {
  flex: 2;
}

.color-picker {
  width: 33px;
  height: 33px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  flex-shrink: 0;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 50%;
}

.score-input {
  width: 80px;
}

.btn-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: #dc3545;
  padding: 6px;
  font-size: 14px;
}

.btn-remove:hover {
  color: #a71d2a;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #fafbfc;
  border-radius: 0 0 12px 12px;
}

.spinner-border-sm {
  width: 16px;
  height: 16px;
  border-width: 2px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  position: relative;
  user-select: none;
}

.checkbox-label input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkmark {
  width: 18px;
  height: 18px;
  background: white;
  border: 2px solid #ddd;
  border-radius: 4px;
  display: inline-block;
  transition: all 0.2s;
}

.checkbox-label input:checked ~ .checkmark {
  background: #667eea;
  border-color: #667eea;
}

.checkbox-label input:checked ~ .checkmark:after {
  content: "✓";
  color: white;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 14px;
}

.text-end {
  text-align: right;
}

.btn-light {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  color: #555;
}

.btn-light:hover {
  background: #e9ecef;
}
</style>